export type QuantityField = 'uom' | 'totalQuantity' | 'achievedQuantity';

export interface QuantityDraft {
  uom?: string | null;
  totalQuantity?: number | null;
  achievedQuantity?: number | null;
}

export interface QuantitySaveBody {
  uom?: string;
  totalQuantity?: number;
  achievedQuantity?: number;
}

export interface QuantitySavePlan {
  /** True when at least one quantity field should be sent. */
  changed: boolean;
  body: QuantitySaveBody;
  errors: Partial<Record<QuantityField, string>>;
}

function ownUom(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
}

function isBlankUomText(value: string | null | undefined): boolean {
  return value != null && String(value).length > 0 && String(value).trim() === '';
}

function sameNumber(a: number | null | undefined, b: number | null | undefined): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return Number.isFinite(a) && Number.isFinite(b) && a === b;
}

export function isConvertedTask(unitActivityDetails: unknown): boolean {
  return Array.isArray(unitActivityDetails) && unitActivityDetails.length > 0;
}

/** Progress to display when a total is set. Null keeps the percent slider in charge. */
export function quantityProgressPercent(
  total: number | null | undefined,
  achieved: number | null | undefined,
): number | null {
  if (total == null || !(total > 0)) return null;
  const done = achieved != null && Number.isFinite(achieved) ? Math.max(0, achieved) : 0;
  return Math.round((done / total) * 100);
}

export function quantityDraftIsDirty(
  baseline: QuantityDraft,
  draft: QuantityDraft,
  opts: { isLeaf: boolean; converted: boolean },
): boolean {
  if (isBlankUomText(draft.uom) || ownUom(baseline.uom) !== ownUom(draft.uom)) return true;
  if (!opts.isLeaf) return false;
  if (!sameNumber(baseline.totalQuantity, draft.totalQuantity)) return true;
  if (!opts.converted && !sameNumber(baseline.achievedQuantity, draft.achievedQuantity)) return true;
  return false;
}

/**
 * Fields to send to the quantity endpoint. Client errors block the call.
 * Clearing a stored value is rejected here; that endpoint treats null as unchanged.
 */
export function buildQuantitySave(
  baseline: QuantityDraft,
  draft: QuantityDraft,
  opts: { isLeaf: boolean; converted: boolean },
): QuantitySavePlan {
  const errors: Partial<Record<QuantityField, string>> = {};
  const body: QuantitySaveBody = {};
  const baseUom = ownUom(baseline.uom);
  const draftUom = ownUom(draft.uom);

  if (isBlankUomText(draft.uom) || (baseUom !== draftUom && !draftUom && baseUom)) {
    errors.uom = 'UOM must not be blank.';
  } else if (draftUom && baseUom !== draftUom) {
    body.uom = draftUom;
  }

  if (opts.isLeaf) {
    const baseTotal = baseline.totalQuantity ?? null;
    const draftTotal = draft.totalQuantity ?? null;
    if (!sameNumber(baseTotal, draftTotal)) {
      if (draftTotal == null || !(draftTotal > 0)) {
        errors.totalQuantity = 'Total quantity must be a number greater than 0.';
      } else {
        body.totalQuantity = draftTotal;
      }
    }

    if (!opts.converted) {
      const baseAchieved = baseline.achievedQuantity ?? null;
      const draftAchieved = draft.achievedQuantity ?? null;
      if (!sameNumber(baseAchieved, draftAchieved)) {
        if (draftAchieved == null || !Number.isFinite(draftAchieved) || draftAchieved < 0) {
          errors.achievedQuantity = 'Achieved quantity must be a number greater than or equal to 0.';
        } else {
          body.achievedQuantity = draftAchieved;
        }
      }
    }
  }

  return {
    changed: Object.keys(body).length > 0,
    body,
    errors,
  };
}

export function isRejectedUomChange(err: unknown): boolean {
  const message = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message.startsWith('Cannot change UOM');
}
